/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import config from '../../../../share/config';
import session from '../../../../share/session';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { REQUEST_STATUS_COMPLETED } from '../../../../share/util/api/polling';
import customTable from '../../../../share/components/vue/PaginationTable';
import projectData from '../../../../share/components/vue/ProjectData';
import shareVehicle from '../../../../share/util/vehicle';
import vinInfoUtils from '../../../../util/vinInfoUtils';
import util from '../../../../share/util/utils';
import diagnoseResource from '../../../../share/util/diagnose';
import apiService from '../../../../share/util/api/service';
import { executeSyncRequest } from '../../../../share/util/api/syncRequest';
import logWrapper from '../../../../share/logWrapper';
const moment = require('moment');

/** 各フェーズ定義 */
const PHASE = {
  DATA_ACQUISITION: 'dataAcquisition',  // 【実行】データ取得
  DATA_DISPLAY: 'dataDisplay',          // 【実行】データ表示
  PROJECT_DATA: 'projectData',          // 【実行】プロジェクトデータ保存
  FAILED: 'failed',                     // 共通エラー
};

export default {
  name: 'requirement172',
  data() {
    return {
      phase: PHASE.DATA_ACQUISITION,
      isLoading: false,
      isDispose: true,
      systemId: '',
      readoutData: [],
      tableItems: [],
      tableColumns: [],
      currentSortKey: 'no',
      diagnoseComment: '',
      dataName: '',
      memo: '',
      vin: null,
      vehicleName: null,
      displayProjectID: '',
      hideRegistrationNumber: true,
      registrationNumber: '',
      functionName: 'individual.individual_diagnosis_function_work_support',
      modal_requirement_172: false,
      exitPromise: null,
      readRequestId: '',
      initialDataName: 'Data1',
      saveCount: 0,
      measuredId: '',
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  components: {
    'custom-table': customTable,
    'project-data': projectData,
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
      vehicleInfo: state => state.vehicle.vehicleInfo,
      vehicleModelDisplayNameList: state => state.masterResources.vehicleModelDisplayNameList,
      language: state => state.systems.language,
    }),

    /** ダイアログヘッダ色 */
    headerColor() {
      const colorMap = {
        [PHASE.DATA_DISPLAY]: 'success',
        [PHASE.FAILED]: 'danger'
      };
      return colorMap[this.phase] ?? 'secondary';
    },

    /** ダイアログヘッダタイトル */
    headerTitle() {
      const titleMap = {
        [PHASE.DATA_ACQUISITION]: 'individual.common_title_execution',
        [PHASE.DATA_DISPLAY]: 'message.common_title_success',
        [PHASE.PROJECT_DATA]: 'individual.label_input',
        [PHASE.FAILED]: 'message.common_title_error',
      };
      return this.$t(titleMap[this.phase] ?? 'message.common_title_confirmation');
    },

    /** ダイアログサイズ */
    modalSize() {
      return this.phase === PHASE.PROJECT_DATA ? 'lg' : 'md';
    },

    /** ダイアログボディメッセージ */
    workSupportMessage() {
      const messageMap = {
        [PHASE.DATA_ACQUISITION]: `${this.i18nWsKey}.REQ_WS_172_TXT_1`,
        [PHASE.DATA_DISPLAY]: '',
        [PHASE.PROJECT_DATA]: '',
        [PHASE.FAILED]: 'individual.error_communication_error',
      };
      const key = messageMap[this.phase];
      return key ? this.$t(key) : '';
    },

    /** テーブル表示フラグ */
    showCustomTable() {
      return this.phase === PHASE.DATA_DISPLAY;
    },

    /** プロジェクトデータ表示フラグ */
    showProjectData() {
      return this.phase === PHASE.PROJECT_DATA;
    },

    /** キャンセルボタン表示フラグ */
    showButtonCancel() {
      return this.phase === PHASE.PROJECT_DATA;
    },

    /** 保存ボタン表示フラグ */
    showButtonSave() {
      return this.phase === PHASE.DATA_DISPLAY || this.phase === PHASE.PROJECT_DATA;
    },

    /** 終了ボタン表示フラグ */
    showButtonFinish() {
      return this.phase === PHASE.DATA_DISPLAY;
    },

    /** OK ボタン表示フラグ */
    showButtonOk() {
      return this.phase === PHASE.FAILED;
    },

    /**
     * 診断系のAPIは複数画面から使用されているため
     * 現在の画面で使用されるAPIが診断/データ再生/個別診断のうちどの機能なのか判別する
     */
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期処理 */
    init() {
      this.modal_requirement_172 = true;
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      if (this.vehicleInfo.vehicle.vin.type === config.VEHICLEINFO_TYPE_VIN) {
        // 登録番号非表示
        this.hideRegistrationNumber = false;
      }
      this.read();
    },

    /** 【実行】データ表示画面表示 */
    changePhaseToDataDisplay() {
      this.phase = PHASE.DATA_DISPLAY;
      // テーブルカラム設定
      this.tableColumns = [
        { name: 'no', label: `${this.i18nWsKey}.label_no` },
        { name: 'timestamp_local', label: `${this.i18nWsKey}.label_timestamp`, originalSortFunc: diagnoseResource.sortDate },
        { name: 'activation_cause_code', label: `${this.i18nWsKey}.label_activation_cause_code` }
      ];
      // テーブル表示データ設定
      this.$nextTick(() => {
        this.tableItems = this.readoutData.map(item => ({
          no: item.number,
          timestamp_local: this.convertLocalDateTime(item.timestamp),
          activation_cause_code: item.activation_cause_code
        }));
      });
    },

    /**
     * GMTからLocalTimeへ変換
     * @param {string} responseDate Backendからの返却timestamp文字列
     * @returns {string} ローカル時刻文字列
     */
    convertLocalDateTime(responseDate) {
      if (!responseDate) {
        return '-';
      }
      const globalDate = new Date(responseDate);
      // 日付として無効な文字列の場合Backendから帰ってきた文字列をそのまま表示する
      if (globalDate.toString() === 'Invalid Date') {
        return responseDate;
      }
      // LocalTimeに変換
      return util.timeConverter(globalDate);
    },

    /** 保存ボタン押下 */
    onSaveClicked() {
      if (this.phase === PHASE.PROJECT_DATA) {
        // プロジェクトデータ保存画面での保存ボタン押下時処理
        this.callSaveEvent();
      } else {
        // 上記以外
        this.screenMovement();
      }
    },

    /** プロジェクトデータ保存画面：保存ボタン押下 */
    callSaveEvent() {
      if (this.$refs.projectData.validate()) {
        // 入力検証OKの場合、プロジェクトデータ保存処理を実行
        this.saveProject();
      }
    },

    /** 終了ボタン押下 */
    async onFinishClicked() {
      await this.terminate();
      this.closeWizard();
    },

    /** ファイルエクスポート（CSV出力） */
    async csvExport() {
      try {
        this.isLoading = true;
        // CSV形式：作動要因コード, 予備, タイムスタンプ(絶対日時)
        const csvRows = this.readoutData.map(item => {
          const tsDate = new Date(item.timestamp);
          let tsStr = '';
          if (tsDate.toString() === 'Invalid Date') {
            // 不正な日付の場合、GMT文字列から作成
            tsStr = item.timestamp.replace(/[-:TZ\s]/g, '').slice(2);
          } else {
            // 上記以外はlocalTimeに変換
            tsStr = util.timeConverter(tsDate).replace(/[:\s/]/g, '').slice(2);
          }
          return `${item.activation_cause_code},${item.reserved},${tsStr}`;
        });
        const csvContent = csvRows.join('\n');
        await util.sleep(1000);
        const fileName = moment().format('YYYYMMDDHHmmss');
        const bomFormatData = new Uint8Array([0xEF, 0xBB, 0xBF]); // UTF-8 BOM
        const blob = new Blob([bomFormatData, csvContent], { type: 'text/csv;charset=utf-8' });
        const link = window.document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `${fileName}.csv`;
        link.style.display = 'none';
        link.click();
        link.remove();
      } finally {
        this.isLoading = false;
      }
    },

    /** 保存ボタン押下：プロジェクトデータ保存画面へ遷移 */
    screenMovement() {
      const vehicle = this.vehicleInfo.vehicle;
      this.vin = vehicle.vin.vin;
      this.displayProjectID = vehicle.project.id;
      const spec = shareVehicle.createModelDisplaySpec(vehicle.vin.type, vehicle.spec);
      this.vehicleName = shareVehicle.createModelDisplayNameCurrentLanguage(
        this.vehicleInfo, this.vehicleModelDisplayNameList, this.language, '{0} {1}', spec
      );
      if (this.hideRegistrationNumber) {
        if (vinInfoUtils.outputRegistrationNumber(vehicle.vin)) {
          this.registrationNumber = vinInfoUtils.createRegistrationNumber(vehicle.vin);
        } else {
          this.registrationNumber = '';
        }
      }

      this.diagnoseComment = '';
      this.memo = '';
      this.dataName = this.createProjectDataName();
      this.phase = PHASE.PROJECT_DATA;
    },

    /**
     * プロジェクトデータ保存画面のデータ名初期値を生成
     * @returns {string} データ名
     */
    createProjectDataName() {
      const matched = this.initialDataName.match(/^Data(\d+)$/);
      if (!matched) {
        return this.initialDataName;
      }

      const baseNumber = Number(matched[1]);
      if (!Number.isFinite(baseNumber)) {
        return this.initialDataName;
      }

      return `Data${baseNumber + this.saveCount}`;
    },

    /**
     * API 呼び出し処理
     * @param {string} calledFrom 呼び出し元
     * @param {function} responseHandler レスポンスハンドラ
     * @param {object} requestBody リクエストボディ追加項目
     * @param {object} options オプション
     * @param {boolean} options.showCommonErrorOnRequestFailure エラー時に共通エラーへ遷移するか
     * @returns {Promise<object|null>} APIレスポンス
     */
    async callApi(calledFrom, responseHandler = () => { }, requestBody = {}, options = {}) {
      try {
        this.isLoading = true;
        const showCommonErrorOnRequestFailure = options.showCommonErrorOnRequestFailure ?? (calledFrom === 'read');
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: calledFrom,
            ...requestBody,
          },
          // エラー発生のコールバック
          (errorType) => {
            errorType;
            if (showCommonErrorOnRequestFailure) {
              this.showUnexpectedError();
            }
          },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result, calledFrom); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result, calledFrom); },
        );

        await responseHandler(response);
        return response;

      } finally {
        this.isLoading = false;
      }
    },

    /** 共通エラー遷移 */
    async showUnexpectedError() {
      // スピナー消えてしまうのを防ぐためPromiseを保持し、各API失敗時に解決を待つ
      this.exitPromise = this.isDispose ? this.dispose() : this.terminate();
      await this.exitPromise;
      this.phase = PHASE.FAILED;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     * @param {string} apiType 呼び出し元API TYPE
     */
    validateErrorResult(result, apiType = '') {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => {
          result;
          // terminate,disposeから呼ばれた場合は共通エラーに遷移しない
          if (apiType === 'terminate' || apiType === 'dispose') return;
          this.showUnexpectedError();
        },
      );
    },

    /** データ読出し処理 */
    async read() {
      await this.callApi(
        'read',
        async (response) => {
          if (!response) {
            // pollingWspApiはエラーコールバックの終了を待たずnullを返す
            // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
            if (this.exitPromise) await this.exitPromise;
            return;
          }

          this.isDispose = response.data.user_data?.is_dispose ?? true;

          if (response.data.status === REQUEST_STATUS_COMPLETED) {
            // 受信データ保持
            this.readoutData = response.data.user_data?.readout_data?.sort((a, b) => {
              return a.number - b.number;
            }) || [];
            this.initialDataName = response.data.user_data?.data_name || 'Data1';
            this.dataName = this.initialDataName;
            this.saveCount = 0;
            this.measuredId = '';
            this.readRequestId = response.data.user_data?.request_id || '';
            // データ表示画面に遷移
            this.changePhaseToDataDisplay();
          } else {
            // 共通エラーへ遷移
            await this.showUnexpectedError();
          }
        }
      );
    },

    /**
     * プロジェクトデータ保存処理
     */
    async saveProject() {
      // 各種データ更新
      this.diagnoseComment = this.$refs.projectData.inputDiagnoseComment;
      this.dataName = this.$refs.projectData.inputDataName;
      this.memo = this.$refs.projectData.inputMemo;

      // save APIを実行し、保存先measured_idを取得する
      await this.callApi(
        'save',
        async (saveResponse) => {
          if (!saveResponse) {
            // pollingWspApiはエラーコールバックの終了を待たずnullを返す
            // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
            if (this.exitPromise) await this.exitPromise;
            return;
          }

          if (saveResponse.data.status !== REQUEST_STATUS_COMPLETED) {
            await this.showUnexpectedError();
            return;
          }

          this.measuredId = saveResponse.data.user_data?.measured_id || '';
          if (this.measuredId === '') {
            logWrapper.log(
              "saveProject: data.user_data.measured_id is not found in response.",
              saveResponse?.data,
              true
            );
            this.changePhaseToDataDisplay();
            return;
          }

          // measured_id取得後に更新APIを呼び出す
          const updateResponse = await executeSyncRequest(
            this,
            // POST API 呼び出しのコールバック
            async () => await apiService.postCall(
              apiService.WSP,
              `/projects/${this.vehicleInfo.vehicle.project.id}/${this.measuredId}`,
              {
                comment: this.diagnoseComment,
                measured: {
                  name: this.dataName,
                  memo: this.memo
                }
              },
              this.operationScreen
            ),
            // エラー発生のコールバック
            async (errorType) => { errorType; await this.showUnexpectedError(); },
            // POST API レスポンスチェックのコールバック
            // レスポンス見ない
            () => { return ''; },
            // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
            async (result) => { await this.validateErrorResult(result); }
          );

          if (!updateResponse) {
            // executeSyncRequestはエラーコールバックの終了を待たずnullを返す
            // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
            if (this.exitPromise) await this.exitPromise;
            return;
          }

          this.saveCount += 1;

          // データ表示画面に遷移
          this.changePhaseToDataDisplay();
        },
        {
          project_id: this.vehicleInfo.vehicle.project.id,
          request_id: this.readRequestId,
        },
        { showCommonErrorOnRequestFailure: true }
      );
    },

    /** Terminate処理 */
    async terminate() {
      await this.callApi('terminate', () => { });
    },

    /** Dispose処理 */
    async dispose() {
      await this.callApi('dispose', () => { });
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_172 = false;
    }
  },

  mounted() {
    this.init();
  }
};
